import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFirestoreInstance,
  getAuthInstance,
  waitForAuthBridge,
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  orderBy,
  limit,
  onSnapshot,
  serverTimestamp,
} from '@/lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import type { Firestore } from 'firebase/firestore';
import type { ChatMessage, GifData, TrackData } from './use-league-chat';

// Re-export types for convenience
export type { ChatMessage, GifData, TrackData };

interface UseDailyChatOptions {
  roomId: number;
  userId: string | null;
  username: string | null;
  avatarUrl: string | null;
  enabled?: boolean;
  messageLimit?: number;
}

interface SendMessageOptions {
  content: string;
  contentType?: 'text' | 'image' | 'gif' | 'track';
  imageUrl?: string;
  gifData?: GifData;
  trackData?: TrackData;
}

interface UseDailyChatReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (options: SendMessageOptions) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  isSending: boolean;
}

/**
 * Hook to manage real-time chat messages for daily rooms using Firestore
 * Adapted from useLeagueChat with dailyRoomChats collection
 */
export function useDailyChat({
  roomId,
  userId,
  username,
  avatarUrl,
  enabled = true,
  messageLimit = 100,
}: UseDailyChatOptions): UseDailyChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const firestoreRef = useRef<Firestore | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Initialize Firestore and set up listener
  useEffect(() => {
    console.log(`📱 [useDailyChat useEffect] entered - enabled: ${enabled}, roomId: ${roomId}`);
    if (!enabled || !roomId) {
      console.log(`📱 [useDailyChat useEffect] early return - enabled: ${enabled}, roomId: ${roomId}`);
      setIsLoading(false);
      return;
    }
    console.log(`📱 [useDailyChat useEffect] proceeding with setup...`);

    let isMounted = true;
    let loadingTimeoutId: NodeJS.Timeout | null = null;

    // Global timeout - if chat doesn't load within 30 seconds, show error
    loadingTimeoutId = setTimeout(() => {
      if (isMounted) {
        console.error('Daily Chat: Global loading timeout reached (30s)');
        setError('Chat took too long to load. Please try again.');
        setIsLoading(false);
      }
    }, 30000);

    const setupListener = async () => {
      const startTime = Date.now();
      const logTiming = (step: string) => {
        console.log(`📱 [Daily Chat Debug] ${step} - ${Date.now() - startTime}ms elapsed`);
      };

      try {
        logTiming('Starting daily chat setup');
        logTiming(`Platform: ${Capacitor.getPlatform()}, isNative: ${Capacitor.isNativePlatform()}`);

        // On native platforms, wait for the auth bridge to complete first
        if (Capacitor.isNativePlatform()) {
          logTiming('Waiting for native auth bridge...');
          const authBridgeTimeout = 15000;
          try {
            await Promise.race([
              waitForAuthBridge(),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Auth bridge timed out')), authBridgeTimeout)
              ),
            ]);
            logTiming('Auth bridge complete');
          } catch (bridgeError: any) {
            logTiming(`Auth bridge failed/timed out: ${bridgeError?.message}`);
          }
        }

        logTiming('Getting Firestore and Auth instances...');
        const initTimeout = 10000;
        const [firestore, auth] = await Promise.race([
          Promise.all([
            getFirestoreInstance(),
            getAuthInstance()
          ]),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Firebase initialization timed out')), initTimeout)
          ),
        ]) as [Firestore | null, any];
        logTiming(`Got Firestore: ${!!firestore}, Auth: ${!!auth}`);

        if (!firestore) {
          throw new Error('Firestore not initialized');
        }
        firestoreRef.current = firestore;

        logTiming('Waiting for auth state...');
        logTiming(`Current auth.currentUser: ${auth.currentUser?.uid ? auth.currentUser.uid.substring(0, 8) + '...' : 'null'}`);

        // Wait for auth state to be determined
        await new Promise<void>((resolve) => {
          const timeout = setTimeout(() => {
            logTiming('Auth state check timed out after 5s, proceeding anyway');
            resolve();
          }, 5000);

          const unsubAuth = onAuthStateChanged(auth, (user) => {
            clearTimeout(timeout);
            unsubAuth();
            if (user) {
              logTiming(`Auth confirmed for user: ${user.email}, uid: ${user.uid?.substring(0, 8)}...`);
            } else {
              logTiming('No authenticated user found');
            }
            resolve();
          }, () => {
            clearTimeout(timeout);
            resolve();
          });
        });

        logTiming('Setting up Firestore listener for daily room chat...');
        // Use dailyRoomChats collection (different from leagueChats)
        const messagesRef = collection(firestore, `dailyRoomChats/${roomId}/messages`);
        const messagesQuery = query(
          messagesRef,
          orderBy('createdAt', 'desc'),
          limit(messageLimit)
        );
        logTiming('Query created, attaching onSnapshot...');

        // Set up real-time listener
        unsubscribeRef.current = onSnapshot(
          messagesQuery,
          (snapshot) => {
            logTiming(`onSnapshot SUCCESS - got ${snapshot.docs.length} messages`);
            if (!isMounted) return;

            const newMessages: ChatMessage[] = [];
            snapshot.forEach((doc) => {
              const data = doc.data();
              newMessages.push({
                id: doc.id,
                userId: data.userId,
                username: data.username,
                avatarUrl: data.avatarUrl,
                content: data.content,
                contentType: data.contentType || 'text',
                imageUrl: data.imageUrl || null,
                gifData: data.gifData || null,
                trackData: data.trackData || null,
                isDeleted: data.isDeleted || false,
                deletedBy: data.deletedBy || null,
                deletedAt: data.deletedAt?.toDate() || null,
                createdAt: data.createdAt?.toDate() || new Date(),
                replyToId: data.replyToId || null,
              });
            });

            // Reverse to show oldest first
            setMessages(newMessages.reverse());
            setIsLoading(false);
            setError(null);
            if (loadingTimeoutId) {
              clearTimeout(loadingTimeoutId);
              loadingTimeoutId = null;
            }
          },
          (err) => {
            if (!isMounted) return;
            console.error('Daily chat listener error:', err);
            if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
              setError('Chat authentication failed. Please sign out and sign back in to access chat.');
            } else {
              setError('Failed to load chat messages');
            }
            setIsLoading(false);
            if (loadingTimeoutId) {
              clearTimeout(loadingTimeoutId);
              loadingTimeoutId = null;
            }
          }
        );
      } catch (err: any) {
        if (!isMounted) return;
        console.error('Daily chat setup error:', err);
        if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
          setError('Chat authentication failed. Please sign out and sign back in to access chat.');
        } else {
          setError('Failed to connect to chat');
        }
        setIsLoading(false);
        if (loadingTimeoutId) {
          clearTimeout(loadingTimeoutId);
          loadingTimeoutId = null;
        }
      }
    };

    setupListener();

    return () => {
      isMounted = false;
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
        loadingTimeoutId = null;
      }
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [roomId, enabled, messageLimit]);

  // Send a message
  const sendMessage = useCallback(
    async (options: SendMessageOptions) => {
      const { content, contentType = 'text', imageUrl, gifData, trackData } = options;

      const needsContent = contentType === 'text';
      const hasRequiredContent = needsContent ? content.trim() : true;

      if (!userId || !username || !firestoreRef.current || !hasRequiredContent) {
        console.warn('Daily chat sendMessage blocked - missing required data:', {
          hasUserId: !!userId,
          hasUsername: !!username,
          hasFirestore: !!firestoreRef.current,
          hasContent: hasRequiredContent,
          contentType
        });
        return;
      }

      setIsSending(true);
      setError(null);

      try {
        // Verify Firebase Auth is still valid before writing
        const auth = await getAuthInstance();
        const currentUser = auth?.currentUser;
        if (!currentUser) {
          throw new Error('Your session has expired. Please refresh the page and sign in again.');
        }

        // Ensure the userId matches the authenticated user
        const authUid = currentUser.uid;
        const writeUserId = authUid; // Always use the Firebase Auth UID for Firestore writes

        console.log('📱 [Daily Chat] Sending message:', {
          roomId,
          authUid,
          propUserId: userId,
          usingUserId: writeUserId,
          contentType,
          contentLength: content.trim().length,
        });

        // Use dailyRoomChats collection
        const messagesRef = collection(firestoreRef.current, `dailyRoomChats/${roomId}/messages`);

        const docRef = await addDoc(messagesRef, {
          userId: writeUserId,
          username,
          avatarUrl,
          content: content.trim().slice(0, 500),
          contentType,
          imageUrl: imageUrl || null,
          gifData: gifData || null,
          trackData: trackData || null,
          isDeleted: false,
          deletedBy: null,
          deletedAt: null,
          createdAt: serverTimestamp(),
          replyToId: null,
        });

        console.log('Daily room message sent successfully:', docRef.id);

      } catch (err: any) {
        console.error('Send daily message error:', err?.code, err?.message, err);

        if (err?.code === 'permission-denied' || err?.message?.includes('permission') || err?.message?.includes('PERMISSION_DENIED')) {
          // Check if it's actually a mute or just an auth issue
          const auth = await getAuthInstance();
          const hasAuth = !!auth?.currentUser;
          console.error('📱 [Daily Chat] Permission denied details:', {
            hasAuth,
            authUid: auth?.currentUser?.uid,
            propUserId: userId,
            roomId,
          });

          if (!hasAuth) {
            setError('Your session expired. Please refresh the page.');
            throw new Error('Session expired - please refresh the page and sign in again.');
          }

          setError('Unable to send message. Please try refreshing the page.');
          throw new Error('Chat permission denied. Please try refreshing the page.');
        }

        setError(err.message || 'Failed to send message');
        throw err;
      } finally {
        setIsSending(false);
      }
    },
    [roomId, userId, username, avatarUrl]
  );

  // Delete a message (soft-delete)
  const deleteMessage = useCallback(
    async (messageId: string) => {
      if (!userId || !firestoreRef.current) return;
      try {
        const messageRef = doc(firestoreRef.current, `dailyRoomChats/${roomId}/messages/${messageId}`);
        await updateDoc(messageRef, {
          isDeleted: true,
          deletedBy: userId,
          deletedAt: serverTimestamp(),
        });
      } catch (err: any) {
        console.error('Delete daily message error:', err?.code, err?.message);
        setError('Failed to delete message');
      }
    },
    [roomId, userId]
  );

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    deleteMessage,
    isSending,
  };
}
